import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import MarkdownViewer from './MarkdownViewer';

interface TextStepProps {
  texto: string;
  onDone?: () => void;
  onWordTyped?: () => void;
  isCompleted?: boolean;
}

const ANIMATION_INTERVAL = 50;

const TextStep: React.FC<TextStepProps> = React.memo(({ texto, onDone, onWordTyped, isCompleted = false }) => {
  const palavras = useMemo(() => texto.split(' '), [texto]);
  const [index, setIndex] = useState(isCompleted ? palavras.length - 1 : 0);
  const doneRef = useRef(isCompleted);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isFirstRenderRef = useRef(true);
  const lastTypedTimeRef = useRef(0);

  const handleWordTyped = useCallback(() => {
    const now = Date.now();
    if (now - lastTypedTimeRef.current >= 100) { // Limita a frequência de chamadas para 10 por segundo
      lastTypedTimeRef.current = now;
      onWordTyped?.();
    }
  }, [onWordTyped]);

  const handleAnimationComplete = useCallback(() => {
    if (!doneRef.current) {
      doneRef.current = true;
      onDone?.();
    }
  }, [onDone]);

  const shouldResetAnimation = useCallback(() => {
    return isFirstRenderRef.current || texto !== palavras.join(' ');
  }, [texto, palavras]);

  const resetAnimation = useCallback(() => {
    setIndex(0);
    doneRef.current = false;
    isFirstRenderRef.current = false;
    lastTypedTimeRef.current = 0;
  }, []);

  const handleNextWord = useCallback(() => {
    setIndex((prev) => {
      const next = prev + 1;
      if (next >= palavras.length) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        handleAnimationComplete();
        return prev;
      }
      handleWordTyped();
      return next;
    });
  }, [palavras.length, handleWordTyped, handleAnimationComplete]);

  const startAnimation = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(handleNextWord, ANIMATION_INTERVAL);
  }, [handleNextWord]);

  const cleanupAnimation = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);

  useEffect(() => {
    if (isCompleted) {
      handleAnimationComplete();
      return;
    }

    if (shouldResetAnimation()) {
      resetAnimation();
    }

    startAnimation();
    return cleanupAnimation;
  }, [palavras, texto, isCompleted, shouldResetAnimation, resetAnimation, startAnimation, cleanupAnimation, handleAnimationComplete]);

  const content = useMemo(() => palavras.slice(0, index + 1).join(' '), [palavras, index]);

  return <MarkdownViewer content={content} />;
});

TextStep.displayName = 'TextStep';

export default TextStep;
